/** Which page numbers a paginator shows, and where it collapses into gaps.
 *
 *  Pure so the window maths can be unit-tested without rendering: a paginator
 *  that silently drops the last page, or renders 40 buttons, is a layout bug
 *  that a render test tends to miss.
 */

/** A rendered slot: a page number, or an elided run of pages. */
export type PageSlot = number | 'gap';

/**
 * Build the visible page list for `page` of `pageCount`.
 *
 * `edge` pages are always shown at each end and `around` pages either side of
 * the current one; everything else collapses to a single 'gap'. A gap is only
 * emitted when it hides MORE than one page — replacing a lone page 4 with an
 * ellipsis costs the same width and takes away a click target.
 */
export function pageWindow(page: number, pageCount: number, edge = 1, around = 1): PageSlot[] {
  if (pageCount <= 0) return [];

  // Below this the windowed form is no narrower than the full list, so
  // collapsing only costs the user click targets.
  const widest = 2 * edge + 2 * around + 3;
  if (pageCount <= widest) return Array.from({ length: pageCount }, (_, i) => i + 1);

  const current = Math.min(Math.max(1, page), pageCount);
  const shown = new Set<number>();
  for (let i = 1; i <= Math.min(edge, pageCount); i += 1) {
    shown.add(i);
    shown.add(pageCount - i + 1);
  }
  for (let i = current - around; i <= current + around; i += 1) {
    if (i >= 1 && i <= pageCount) shown.add(i);
  }

  const slots: PageSlot[] = [];
  let previous = 0;
  for (const n of [...shown].sort((a, b) => a - b)) {
    const skipped = n - previous - 1;
    if (skipped === 1) slots.push(previous + 1);
    else if (skipped > 1) slots.push('gap');
    slots.push(n);
    previous = n;
  }
  return slots;
}
